import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSettings } from '../contexts/SettingsContext';

// AI Automation SSE URL — direct connection (no Core proxy needed for SSE)
const AI_AUTOMATION_SSE_URL = process.env.REACT_APP_AI_AUTOMATION_URL || 'http://127.0.0.1:8100/aibot/v1';

/**
 * ResumeProgressBar Component
 * Displays real-time progress of resume parsing operations via SSE.
 * Falls back to polling if SSE connection fails.
 *
 * @param {string} userId - User ID for progress tracking
 * @param {string} cid - Company ID for progress tracking
 * @param {function} onComplete - Callback when processing completes
 * @param {function} onProgressUpdate - Callback for progress updates
 * @param {number} triggerCheck - Trigger to start checking progress
 */
const ResumeProgressBar = ({ userId, cid, onComplete, onProgressUpdate, triggerCheck }) => {
  const { settings } = useSettings();
  const primaryColor = settings?.theme?.colors?.primary || '#3B5BDB';
  const accentColor = settings?.theme?.colors?.accent || '#845EF7';

  const [progress, setProgress] = useState({
    status: 'idle',
    completed: 0,
    total: 0,
    percentage: 0,
  });

  const [isVisible, setIsVisible] = useState(false);
  const [queuedRetry, setQueuedRetry] = useState(0);
  const [circuitBreaker, setCircuitBreaker] = useState('closed');

  // Track high watermarks — never let counts go down during a session
  const highWaterRef = useRef({ total: 0, completed: 0, failed: 0 });
  const fileLogMapRef = useRef({});

  const eventSourceRef = useRef(null);
  const completionFiredRef = useRef(false);
  const lastCompletedTimeRef = useRef(0);

  // Stable refs for callbacks — prevents SSE restart when parent re-renders
  const onCompleteRef = useRef(onComplete);
  const onProgressUpdateRef = useRef(onProgressUpdate);
  onCompleteRef.current = onComplete;
  onProgressUpdateRef.current = onProgressUpdate;

  // Close SSE connection
  const closeSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // Handle incoming SSE data
  const handleSSEData = useCallback((data) => {
    // Apply high watermark — never let counts go down (chunks complete and get cleaned up)
    const hw = highWaterRef.current;
    const rawTotal = data.total || 0;
    const rawCompleted = data.completed || 0;
    const rawFailed = data.failed || 0;
    const incomingLogs = Array.isArray(data.file_logs) ? data.file_logs : [];

    incomingLogs.forEach((log, idx) => {
      const key = log?.file || `unknown_${idx}`;
      if (!key) return;
      fileLogMapRef.current[key] = log;
    });

    const mergedFileLogs = Object.values(fileLogMapRef.current);
    const logCompleted = mergedFileLogs.filter(log => ['completed', 'duplicate', 'skipped'].includes(log?.status)).length;
    const logFailed = mergedFileLogs.filter(log => log?.status === 'failed').length;
    const logQueuedRetry = mergedFileLogs.filter(log => log?.status === 'queued_retry').length;
    const logTotal = mergedFileLogs.length;

    hw.total = Math.max(hw.total, rawTotal, logTotal);
    hw.completed = Math.max(hw.completed, rawCompleted, logCompleted);
    hw.failed = Math.max(hw.failed, rawFailed, logFailed);

    const adjustedTotal = hw.total;
    const adjustedCompleted = hw.completed;
    const adjustedFailed = hw.failed;
    const duplicatesUpdated = data.duplicates_updated || 0;
    const adjustedNewResumes = Math.max(data.new_resumes || 0, adjustedCompleted - duplicatesUpdated);
    const adjustedQueuedRetry = Math.max(data.queued_retry || 0, data.progress?.queued_retry || 0, logQueuedRetry);
    const adjustedPercentage = adjustedTotal > 0
      ? Math.round(((adjustedCompleted + adjustedFailed + adjustedQueuedRetry) / adjustedTotal) * 100)
      : 0;

    const accountedCount = adjustedCompleted + adjustedFailed + adjustedQueuedRetry;
    const isActuallyComplete = adjustedTotal > 0 && accountedCount >= adjustedTotal;
    const normalizedStatus = data.status === 'completed' && !isActuallyComplete
      ? 'processing'
      : data.status;

    const progressData = {
      status: normalizedStatus,
      progress: {
        completed: adjustedCompleted,
        failed: adjustedFailed,
        total: adjustedTotal,
        percentage: adjustedPercentage,
        new_resumes: adjustedNewResumes,
        duplicates_updated: duplicatesUpdated,
        queued_retry: adjustedQueuedRetry,
      },
      file_logs: mergedFileLogs,
      queued_retry: adjustedQueuedRetry,
    };

    setProgress(progressData);
    setQueuedRetry(adjustedQueuedRetry);
    setCircuitBreaker(data.circuit_breaker || 'closed');

    if (normalizedStatus === 'processing' && adjustedTotal > 0) {
      setIsVisible(true);
    }

    if (onProgressUpdateRef.current) onProgressUpdateRef.current(progressData);

    // Handle completion
    if (normalizedStatus === 'completed') {
      if (completionFiredRef.current) return;
      completionFiredRef.current = true;
      lastCompletedTimeRef.current = Date.now();
      closeSSE();

      setTimeout(() => {
        setIsVisible(false);
        if (onCompleteRef.current) onCompleteRef.current(progressData);
      }, 5000);
    }
  }, [closeSSE]);

  // Start SSE connection
  const startSSE = useCallback(() => {
    if (!userId || !cid) return;
    if (eventSourceRef.current) return; // Already connected

    completionFiredRef.current = false;
    highWaterRef.current = { total: 0, completed: 0, failed: 0 }; // Reset for new session
    fileLogMapRef.current = {};
    const url = `${AI_AUTOMATION_SSE_URL}/resumeparse/progress-stream/${userId}/${cid}`;

    try {
      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleSSEData(data);
        } catch (e) {
          console.error('SSE parse error:', e);
        }
      };

      es.addEventListener('complete', () => {
        // onmessage decides whether the session is truly complete. Some
        // chunked uploads briefly emit a completed event for a partial batch.
        if (completionFiredRef.current) closeSSE();
      });

      es.addEventListener('timeout', () => {
        setIsVisible(false);
        closeSSE();
      });

      es.onerror = () => {
        console.warn('SSE connection error — will auto-reconnect');
        // EventSource auto-reconnects; no manual action needed
      };
    } catch (err) {
      console.error('Failed to create EventSource:', err);
    }
  }, [userId, cid, handleSSEData, closeSSE]);

  // Auto-resume from server on mount (survives page refresh)
  useEffect(() => {
    if (!userId || !cid) return;
    const checkServer = async () => {
      try {
        const url = `${AI_AUTOMATION_SSE_URL}/resumeparse/upload-logs/${encodeURIComponent(userId)}/${encodeURIComponent(cid)}`;
        const response = await fetch(url);
        if (!response.ok) return;
        const data = await response.json();
        if (data.status === 'processing' && data.total > 0) {
          setProgress({
            status: 'processing',
            progress: {
              completed: data.completed || 0,
              failed: data.failed || 0,
              total: data.total,
              percentage: data.total > 0 ? Math.round(((data.completed + data.failed) / data.total) * 100) : 0,
              new_resumes: 0,
              duplicates_updated: 0,
            },
            file_logs: data.file_logs || [],
          });
          setIsVisible(true);
          startSSE();
        }
      } catch (_) { /* ignore */ }
    };
    checkServer();
  }, [userId, cid, startSSE]);

  // Start SSE when triggerCheck changes (upload starts)
  useEffect(() => {
    if (triggerCheck && userId && cid) {
      const timeSinceComplete = Date.now() - lastCompletedTimeRef.current;
      if (timeSinceComplete < 10000 && completionFiredRef.current) return;

      closeSSE();
      completionFiredRef.current = false;
      setIsVisible(true);
      startSSE();
    }
  }, [triggerCheck, userId, cid, startSSE, closeSSE]);

  // Cleanup on unmount
  useEffect(() => {
    return () => closeSSE();
  }, [closeSSE]);

  if (!isVisible || !progress || progress.status === 'idle') {
    return null;
  }

  const isCompleted = progress.status === 'completed' || (progress.progress?.percentage === 100);
  const newResumes = progress?.progress?.new_resumes || 0;
  const duplicatesUpdated = progress?.progress?.duplicates_updated || 0;
  const failedCount = progress?.progress?.failed || 0;
  const totalCompleted = progress?.progress?.completed || 0;
  const totalCount = progress?.progress?.total || 0;

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border shadow-sm transition-all duration-300 ${
        isCompleted ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : ''
      }`}
      style={{
        minWidth: '200px',
        maxWidth: '300px',
        ...(!isCompleted ? {
          backgroundColor: `${primaryColor}08`,
          borderColor: `${primaryColor}30`,
        } : {}),
      }}
    >
      {/* Status icon */}
      <div
        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: isCompleted ? '#22C55E' : primaryColor }}
      >
        {isCompleted ? (
          <svg width="12" height="12" fill="white" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ) : (
          <div className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {/* Status text with summary when completed */}
      {isCompleted ? (
        <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <span className="font-semibold text-green-700 dark:text-green-300">Done</span>
          <span className="text-green-600 dark:text-green-400 flex items-center gap-1.5">
            {newResumes > 0 && (
              <span className="flex items-center gap-0.5">
                <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>{newResumes}</span>
              </span>
            )}
            {duplicatesUpdated > 0 && (
              <span className="flex items-center gap-0.5">
                <svg className="w-3 h-3" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{duplicatesUpdated}</span>
              </span>
            )}
            {failedCount > 0 && (
              <span className="flex items-center gap-0.5 text-red-500">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>{failedCount}</span>
              </span>
            )}
          </span>
        </div>
      ) : (
        <span className="text-xs font-semibold whitespace-nowrap" style={{ color: primaryColor }}>
          Processing
        </span>
      )}

      {/* Progress bar */}
      <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden min-w-[60px] relative">
        <div
          className="h-full transition-all duration-500 ease-in-out rounded-full"
          style={{
            width: `${progress?.progress?.percentage || 0}%`,
            background: isCompleted
              ? 'linear-gradient(to right, #4ADE80, #22C55E)'
              : `linear-gradient(to right, ${primaryColor}, ${accentColor})`,
          }}
        />
      </div>

      {/* Progress count */}
      <span
        className="text-xs font-semibold whitespace-nowrap"
        style={{ color: isCompleted ? '#22C55E' : primaryColor }}
      >
        {totalCompleted}/{totalCount}
      </span>

      {/* Queued retry indicator */}
      {queuedRetry > 0 && (
        <span className="text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap" title="Resumes queued for retry due to API limit">
          +{queuedRetry} queued
        </span>
      )}
    </div>
  );
};

export default ResumeProgressBar;
